import classNames from 'classnames';
import { toNumber } from 'lodash';
import type { FC } from 'react';
import { useContext } from 'react';
import { useRef, useState } from 'react';
import React from 'react';

import CartPortal from '../components/CartPortal';
import ContentNavigation from '../components/ContentNavigation';
import ProductInfoCard from '../components/ProductInfoCard';
import ServiceProviderLogo from '../components/ServiceProviderLogo';
import ShippingReasons from '../components/ShippingReasons';
import BoxModal from '../components/ui/BoxModal';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import '../components/ui/Input.scss';
import Textarea from '../components/ui/Textarea';
import ConfigContext from '../service/context/config';
import useAppStore from '../service/store/app';
import useOrderStore from '../service/store/order';
import { COST_APPROVAL_FREE_LIMIT } from '../utils/constants';
import { scrollToError, scrollToRef } from '../utils/scroll';
import { getServiceProvider } from '../utils/service_provider';

const StepProductInfo: FC = () => {
  const config = useContext(ConfigContext);
  const costApprovalAlertRef = useRef<null | HTMLDivElement>(null);
  const errorRef = useRef<null | HTMLDivElement>(null);
  const [ showCostApprovalAlert, setShowCostApprovalAlert ] = useState(false);
  const [ showErrors, setShowErrors ] = useState(false);
  const {
    leak_pipe_dimension,
    pipe_system_name_manufacturer,
    // leak_solution,
    description,
    cost_approval,
    isComplaint,
    complaintCategory,
    serialNumber,
    shipping_reasons,
    shipping_reason_other
  } = useOrderStore(state => state.order) || {};
  const onlyAccessoriesSelected = useAppStore(state => state.onlyAccessoriesSelected);
  const buttonText = onlyAccessoriesSelected ? 'Weiter zur Zubehörauswahl' : 'Weiter zu Zubehör';

  const setOrderValue = useOrderStore(state => state.setValue);
  const setShippingReasons = useOrderStore(state => state.setShippingReasons);
  const setStep = useAppStore(state => state.setStep);
  const { name: serviceProviderName } = getServiceProvider();
  const showComplaintButtons = !!config?.complaint_form;

  let isValid = false;

  if (description) {
    if (!isComplaint) {
      isValid = true;
    } else if (complaintCategory) {
      if (complaintCategory !== 'Undichte') {
        isValid = true;
      // } else if (leak_pipe_dimension && pipe_system_name_manufacturer && leak_solution) {
      } else if (leak_pipe_dimension && pipe_system_name_manufacturer) {
        isValid = true;
      }
    }
  }

  // Der Grund der Einsendung ist Pflicht -- aber nur, wenn tatsaechlich ein
  // Geraet eingeschickt wird (bei reiner Zubehoerauswahl gibt es keines).
  const hasShippingReason = onlyAccessoriesSelected || (shipping_reasons || []).length > 0;

  if (!hasShippingReason) {
    isValid = false;
  }

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const valueToSet = event.target.type === 'range' ? parseInt(event.target.value, 10) : event.target.value;

    setOrderValue(valueToSet, event.target.name);
  };

  const handleCtaClick = (): void => {
    if (isValid) {
      if (
        toNumber(cost_approval) >= COST_APPROVAL_FREE_LIMIT
        || showCostApprovalAlert
      ) {
        void setStep(4);
        scrollToRef();
      } else {
        setShowCostApprovalAlert(true);

        setTimeout(() => {
          costApprovalAlertRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 200);
      }
    } else {
      setShowErrors(true);
      scrollToError(errorRef);
    }
  };

  return (
    <>
      <ContentNavigation>
        <Button
          onClick={handleCtaClick}
          className={classNames('btn-dark', {
            'btn-disabled': !isValid
          })}
          hasRightIcon
          // disabled={!description}
        >
          {buttonText}
        </Button>
      </ContentNavigation>
      <div className="box">
        {serviceProviderName && (
          <div className="box-header">
            <div className="service-provider-logo">
              <ServiceProviderLogo />
            </div>
            Dieses Gerät wird von {serviceProviderName} bearbeitet.
          </div>
        )}
        <div className="box-content">
          <div ref={errorRef}>
            {showErrors && !isValid && (
              <div className="error-box">
                Bitte fülle alle Pflichtfelder aus.
              </div>
            )}
          </div>
          <div className="form-grid">
            <div className="styled-input">
              <label>Deine Auswahl:</label>
              <div className="item-box">
                <ProductInfoCard />
              </div>
            </div>
            <div className="styled-input styled-input-input-range">
              <label>Kostenfreigabe für sofortige Reparatur:</label>
              <div className="item-box">
                <div className="input-range-wrap">
                  <div>0€</div>
                  <input
                    type="range"
                    name="cost_approval"
                    value={cost_approval}
                    onChange={handleInputChange}
                    step="10"
                    min="0"
                    max="700"
                  />
                  <div>{cost_approval}€</div>
                </div>
              </div>
            </div>
          </div>
          {showComplaintButtons && (
            <>
              <div className="form-grid">
                <div className="styled-input">
                  <div className="styled-input">
                    <label>
                      {/* eslint-disable-next-line max-len */}
                      Handelt es sich um eine Herstellergarantie, Reklamation oder wurden undichte Verpressungen erstellt?
                    </label>
                    <div className="buttons-wrap">
                      <button
                        className={classNames({ active: isComplaint })}
                        onClick={() => setOrderValue(true, 'isComplaint')}
                      >
                        Ja
                      </button>
                      <button
                        className={classNames({ active: !isComplaint })}
                        onClick={() => setOrderValue(false, 'isComplaint')}
                      >
                        Nein
                      </button>
                    </div>
                  </div>
                  {isComplaint && (
                    <>
                      <div className={classNames('styled-input', {
                        'has-error': showErrors && !complaintCategory
                      })}>
                        <label>Zu welcher Kategorie gehört der Fall?</label>
                        <div className="buttons-wrap">
                          <button
                            className={classNames({ active: complaintCategory === 'Herstellergarantie' })}
                            onClick={() => setOrderValue('Herstellergarantie', 'complaintCategory')}
                          >
                            Herstellergarantie
                          </button>
                          <button
                            className={classNames({ active: complaintCategory === 'Reklamation' })}
                            onClick={() => setOrderValue('Reklamation', 'complaintCategory')}
                          >
                            Reklamation
                          </button>
                          <button
                            className={classNames({ active: complaintCategory === 'Undichte' })}
                            onClick={() => setOrderValue('Undichte', 'complaintCategory')}
                          >
                            Undichte
                          </button>
                        </div>
                      </div>
                      {complaintCategory === 'Undichte' && (
                        <div>
                          <Input
                            label="Mit welcher Rohrdimension trat diese Undichtigkeit auf? *"
                            // eslint-disable-next-line max-len
                            placeholder="Bitte lassen sie uns alle Werkzeuge, welche zur Verursachung der Undichtigkeit maßgeblich gewesen sein können, zukommen."
                            name="leak_pipe_dimension"
                            // eslint-disable-next-line max-len
                            onChange={handleInputChange as React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement>}
                            value={leak_pipe_dimension}
                            wrapperClass={classNames({
                              'has-error': showErrors && !leak_pipe_dimension
                            })}
                          />
                          <Input
                            label="Welches Rohrverbundsystem (Name des Systems + Hersteller) wurde verwendet? *"
                            name="pipe_system_name_manufacturer"
                            value={pipe_system_name_manufacturer}
                            wrapperClass={classNames({
                              'has-error': showErrors && !pipe_system_name_manufacturer
                            })}
                            // eslint-disable-next-line max-len
                            onChange={handleInputChange as React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement>}
                          />
                          <div>
                            <div className="text-14 mt-2">
                              <strong>Hinweis:</strong><br />
                              <span className="text-base-light">
                                {/* eslint-disable-next-line max-len */}
                                Einen wirtschaftlichen Schaden kannst du gerne an den jeweiligen Rohr- oder Fittinghersteller melden.
                                <br />
                                {/* eslint-disable-next-line max-len */}
                                Im ersten Schritt erstellen wir auf deine Meldung einer undichten Verpressung ein Gutachten zu den betroffenen Bauteilen im Rahmen einer Inspektion.
                              </span>
                            </div>
                            {/* <label*/}
                            {/*  className={classNames(*/}
                            {/*    { 'error-color': showErrors && !leak_solution }*/}
                            {/*  )}*/}
                            {/* >*/}
                            {/*  Haben Sie das Problem mit der Undichten Pressverbindung bereits gelöst? **/}
                            {/* </label>*/}
                            {/* <div className="radio-option-wrap">*/}
                            {/*  {*/}
                            {/*    [*/}
                            {/*      'Ja, es wurde nachverpresst',*/}
                            {/*      'Ja, es wurde ausgetauscht - Bauteil noch vorhanden',*/}
                            {/*      'Ja, es wurde ausgetauscht - Bauteil nicht mehr vorhanden',*/}
                            {/*      'Nein, wir haben nichts weiter unternommen'*/}
                            {/*    ].map(option => (*/}
                            {/*      <div key={option} className="styled-input-checkbox radio-option">*/}
                            {/*        <label htmlFor={option}>*/}
                            {/*          <input*/}
                            {/*            type="radio"*/}
                            {/*            id={option}*/}
                            {/*            name="leak_solution"*/}
                            {/*            value={option}*/}
                            {/*            checked={option === leak_solution?.toString()}*/}
                            {/*            onChange={*/}
                            {/*              // eslint-disable-next-line max-len*/}
                            {/* eslint-disable-next-line max-len */}
                            {/*              handleInputChange as React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement>*/}
                            {/*            }*/}
                            {/*          />*/}
                            {/*          {option}*/}
                            {/*        </label>*/}
                            {/*      </div>*/}
                            {/*    ))*/}
                            {/*  }*/}
                            {/* </div>*/}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
          <div className="form-grid">
            <div>
              <Textarea
                value={description}
                name="description"
                onChange={handleInputChange}
                wrapperClass={classNames({
                  'has-error': showErrors && !description
                })}
                label="Beschreiben Sie ihr Anliegen:*"
                placeholder="z.B kurze Fehlerbeschreibung: Meine Presse presst nicht..."
              />
            </div>
          </div>
          {!onlyAccessoriesSelected && (
            <>
              <div className="form-grid">
                <div>
                  <Input
                    value={serialNumber}
                    label="Herstellerseriennummer"
                    name="serialNumber"
                    onChange={handleInputChange as React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement>}
                  />
                </div>
              </div>
              <div className="form-grid">
                <div>
                  <ShippingReasons
                    values={shipping_reasons}
                    other={shipping_reason_other}
                    onToggle={setShippingReasons}
                    onOtherChange={
                      handleInputChange as React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement>
                    }
                    hasError={showErrors}
                  />
                </div>
              </div>
              <div className="form-grid">
                <div>
                  <label>Du brauchst ein Überbrückungsgerät? Dann sprich uns gerne direkt an!</label>
                  <div className="text-14 mt-2">
                    <strong>Hinweis:</strong><br />
                    <span className="text-base-light">In der Regel dauert der Service einer Maschine
                      in unserer Werkstatt nur ein paar Tage. Falls du das Gerät während dieser Zeit brauchst,
                      geben wir dir ein ähnliches Mietgerät.</span>
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="box-cta">
            <Button
              onClick={handleCtaClick}
              className={classNames({
                'btn-disabled': !isValid
              })}
              hasRightIcon
              icon="arrow-right"
              // disabled={!description}
            >
              {buttonText}
            </Button>
          </div>
          <CartPortal>
            <Button
              onClick={handleCtaClick}
              className={classNames({
                'btn-disabled': !isValid
              })}
              hasRightIcon
              icon="arrow-right"
              // disabled={!description}
            >
              Weiter zu Zubehör
            </Button>
          </CartPortal>
          {showCostApprovalAlert && (
            <BoxModal
              ref={costApprovalAlertRef}
              text="
              Mit einer Kostenfreigabe können wir nach der Diagnose sofort weiterarbeiten, was Zeit und Geldspart.
              Sollte die Freigabe überschritten werden, erhalten Sie das Angebot kostenlos.
              Großhändler erhalten Angebote generell kostenfrei."
              title="Schon gewusst?"
              btnText={buttonText}
              secondaryCtaText="Kostenfreigabe anpassen"
              btnIcon="arrow-right"
              closeFunction={() => {
                setShowCostApprovalAlert(false);
              }}
              successFunction={handleCtaClick}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default StepProductInfo;
